/* ------------------------------------------------------------------
   gradientAnimator.js – final fix (no NaN, no undefined on load)
   ------------------------------------------------------------------ */
(function () {
  class GradientAnimator {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx    = canvas.getContext('2d');

      /* ↓ establish a safe default BEFORE first resize() */
      this.type         = 'linear';
      this.angle        = 0;
      this.currentStops = [
        { color: '#000000', position: 0 },
        { color: '#000000', position: 1 }
      ];

      this.targetStops  = [];
      this.duration     = 1000;
      this.progress     = 0;
      this.running      = false;
      this.startTime    = null;
      this.frame        = null;

      this.resize();                                 // <- first paint
      window.addEventListener('resize', () => this.resize());
    }

    /* ---------- colour helpers ---------- */
    static hexToRgb(hexStr){
      let hex = hexStr.replace('#','');
      if (![3,4,6,8].includes(hex.length)) return null;
      if (hex.length<=4) hex = hex.split('').map(c=>c+c).join('').slice(0,6);
      const n = parseInt(hex.slice(0,6),16);
      return { r:(n>>16)&255, g:(n>>8)&255, b:n&255 };
    }
    static cssToRgb(str){
      str = str.trim();
      if (str.startsWith('#'))   return this.hexToRgb(str);
      if (str.startsWith('rgb')){
        const m = str.match(/\d+/g);
        return m ? { r:+m[0], g:+m[1], b:+m[2] } : null;
      }
      return null;
    }

    /* ---------- size & immediate redraw ---------- */
    resize(){
      this.width  = this.canvas.offsetWidth;
      this.height = this.canvas.offsetHeight;
      this.canvas.width  = this.width;
      this.canvas.height = this.height;

      /* paint *something* even if stops not ready */
      const stops = this.currentStops?.length ? this.currentStops
        : [{color:'#000',position:0},{color:'#000',position:1}];
      this.drawGradient(stops);
    }

    /* ---------- public API ---------- */
    setGradient(str, duration=1000){
      const parsed = this.parse(str);
      if (!parsed) return;

      this.type        = parsed.type;
      this.angle       = parsed.angle;
      this.startStops  = [...this.currentStops];
      this.targetStops = parsed.stops;
      this.duration    = duration;
      this.progress    = 0;
      this.startTime   = null;

      /* equalise lengths */
      while (this.startStops.length < this.targetStops.length){
        this.startStops.push({ ...this.startStops[this.startStops.length-1] });
      }
      while (this.targetStops.length < this.startStops.length){
        this.targetStops.push({ ...this.targetStops[this.targetStops.length-1] });
      }

      if (!this.running){
        this.running = true;
        this.animate();
      }
    }

    /* ---------- gradient string → object ---------- */
    parse(str){
      str = str.trim().replace(/;$/,'');
      const linear = str.match(/^linear-gradient\(([^)]+)\)$/i);
      const radial = str.match(/^radial-gradient\(([^)]+)\)$/i);
      if (!linear && !radial) return null;

      const type    = linear ? 'linear' : 'radial';
      const content = (linear||radial)[1];
      const parts   = content.split(/,(?![^(]*\))/).map(s=>s.trim());

      let angle='180deg', stops=parts;
      if (type==='linear' && (parts[0].includes('deg')||parts[0].includes('to '))){
        angle=parts.shift(); stops=parts;
      }

      const parsedStops = stops
        .map(p=>{
          const col = p.match(/#(?:[0-9a-f]{3,8})|rgba?\([^)]+\)/i)?.[0]||'';
          const pos = p.match(/\b\d+%/)?.[0]||null;
          const rgb = GradientAnimator.cssToRgb(col);
          return rgb ? { color:col, position:pos?parseFloat(pos)/100:null } : null;
        })
        .filter(Boolean);
      if (!parsedStops.length) return null;

      if (parsedStops.some(s=>s.position===null)){
        parsedStops.forEach((s,i)=>{s.position=i/(parsedStops.length-1);});
      }

      return { type, angle:this.angleToRad(angle), stops:parsedStops };
    }

    angleToRad(a){
      if (a.includes('deg')) return parseFloat(a)*(Math.PI/180);
      const dir=a.split('to ')[1]||'';
      return {right:0,bottom:Math.PI/2,left:Math.PI,top:3*Math.PI/2}[dir] ?? Math.PI/2;
    }

    /* ---------- main loop ---------- */
    animate(){
      const now=performance.now();
      if (!this.startTime) this.startTime=now;
      const t=Math.min((now-this.startTime)/this.duration,1);
      this.progress=t;

      const tween=this.startStops.map((s,i)=>({
        color:this.mix(s.color,this.targetStops[i].color,t),
        position:this.targetStops[i].position
      }));
      this.drawGradient(tween);

      if (t<1) this.frame=requestAnimationFrame(()=>this.animate());
      else{
        this.currentStops=[...this.targetStops];
        this.running=false;
      }
    }

    /* ---------- drawing ---------- */
    drawGradient(stops){
      let g;
      if (this.type==='linear'){
        const x1=this.width/2+Math.cos(this.angle+Math.PI)*this.width,
              y1=this.height/2+Math.sin(this.angle+Math.PI)*this.height,
              x2=this.width/2+Math.cos(this.angle)*this.width,
              y2=this.height/2+Math.sin(this.angle)*this.height;
        g=this.ctx.createLinearGradient(x1,y1,x2,y2);
      }else{
        g=this.ctx.createRadialGradient(
          this.width/2,this.height/2,0,
          this.width/2,this.height/2,Math.max(this.width,this.height)
        );
      }

      stops.forEach(s=>{
        const rgb=GradientAnimator.cssToRgb(s.color)||{r:0,g:0,b:0};
        const c=`rgb(${rgb.r},${rgb.g},${rgb.b})`;
        try{ g.addColorStop(s.position,c);}catch{}
      });
      this.ctx.fillStyle=g;
      this.ctx.fillRect(0,0,this.width,this.height);
    }

    mix(a,b,t){
      const c1=GradientAnimator.cssToRgb(a)||{r:0,g:0,b:0};
      const c2=GradientAnimator.cssToRgb(b)||{r:0,g:0,b:0};
      const L=(m,n)=>Math.round(m+(n-m)*t);
      return `rgb(${L(c1.r,c2.r)},${L(c1.g,c2.g)},${L(c1.b,c2.b)})`;
    }
  }

  window.GradientAnimator = GradientAnimator;
})();
